import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminService } from '../../services/adminService';

export default function PromptsTab() {
  const [promptsList, setPromptsList] = useState(null);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const data = await adminService.getPrompts('system');
      setPromptsList(data.prompts || []);
    } catch (e) {
      setPromptsList([]);
    } finally {
      setPromptsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">Системные промпты</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPrompts} disabled={promptsLoading}>
            {promptsLoading ? 'Загрузка...' : 'Обновить'}
          </Button>
          <Button size="sm" className="bg-paper-accent hover:bg-paper-accent-hover" onClick={() => setEditingPrompt({ name: '', text: '', _isNew: true })}>
            <Plus className="w-4 h-4 mr-1" /> Создать
          </Button>
        </div>
      </div>

      {promptsLoading && !promptsList && (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-paper-accent" />
        </div>
      )}

      {promptsList && promptsList.length === 0 && (
        <p className="text-paper-hint text-center py-8">Промпты не найдены</p>
      )}

      {promptsList && promptsList.length > 0 && (
        <div className="grid gap-3">
          {promptsList.map((prompt) => (
            <Card key={prompt._id} className="p-4">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm mb-1">{prompt.name}</p>
                  <p className="text-sm text-paper-ink-3 line-clamp-2">{prompt.text}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setEditingPrompt({ ...prompt })}>
                    Изменить
                  </Button>
                  <button
                    className="text-paper-hint hover:text-paper-accent transition-colors p-1"
                    title="Удалить промпт"
                    onClick={async () => {
                      if (!window.confirm(`Удалить промпт "${prompt.name}"?`)) return;
                      try {
                        await adminService.deletePrompt(prompt._id);
                        await fetchPrompts();
                      } catch (err) {
                        alert('Ошибка удаления: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create modal */}
      {editingPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditingPrompt(null)}>
          <div className="bg-paper-card rounded-card shadow-[0_24px_64px_rgba(0,0,0,.18)] w-full max-w-lg mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">
              {editingPrompt._isNew ? 'Создать промпт' : 'Редактировать промпт'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-paper-ink-2 mb-1">Название</label>
                <Input
                  value={editingPrompt.name}
                  onChange={(e) => setEditingPrompt({ ...editingPrompt, name: e.target.value })}
                  placeholder="Название промпта"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-paper-ink-2 mb-1">Текст</label>
                <textarea
                  className="w-full rounded-chip bg-paper-bg px-3 py-2 text-sm min-h-[200px] resize-y focus:outline-none focus:ring-2 focus:ring-paper-accent focus:border-transparent"
                  value={editingPrompt.text}
                  onChange={(e) => setEditingPrompt({ ...editingPrompt, text: e.target.value })}
                  placeholder="Текст системного промпта..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditingPrompt(null)}>Отмена</Button>
              <Button
                className="bg-paper-accent hover:bg-paper-accent-hover"
                disabled={!editingPrompt.name.trim() || !editingPrompt.text.trim()}
                onClick={async () => {
                  try {
                    if (editingPrompt._isNew) {
                      await adminService.createPrompt(editingPrompt.name, editingPrompt.text);
                    } else {
                      await adminService.updatePrompt(editingPrompt._id, {
                        name: editingPrompt.name,
                        text: editingPrompt.text,
                      });
                    }
                    setEditingPrompt(null);
                    await fetchPrompts();
                  } catch (err) {
                    alert('Ошибка сохранения: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
                  }
                }}
              >
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
